import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { useApi } from '@app/hooks';
import { QueryTable } from '@app/components/common/table';
import { QueryTableParams } from '@app/components/common/table/types';
import { useHeader } from '@app/components/common/navigation/hooks/header-service';

import { DistributorSkuRow, useColumns } from './columns';
import { EditDistributorSku } from './edit';

const toBooleanFilter = (value: string | string[] | number[] | undefined) => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  return String(value) === '1';
};

export const DistributorSkusTable: FC = () => {
  useHeader('SKU Дистрибьюторов', []);

  const api = useApi();
  const distributorNamesRef = useRef<Map<number, string>>(new Map());
  const distributorsLoadedRef = useRef(false);
  const [editOpen, setEditOpen] = useState(false);
  const [current, setCurrent] = useState<DistributorSkuRow>();

  const columns = useColumns({
    onEdit: (item) => {
      setCurrent(item);
      setEditOpen(true);
    },
  });

  const loadDistributors = useCallback(async () => {
    if (distributorsLoadedRef.current) {
      return;
    }

    const response = await api.distributors.getDistributors({
      limit: 100000,
      offset: 0,
      sort: '+id',
      filter: {},
    });

    distributorNamesRef.current = new Map(
      (response?.data?.data || []).map((item) => [
        Number(item.id),
        item.name || item.legal_name || '',
      ])
    );
    distributorsLoadedRef.current = true;
  }, [api]);

  useEffect(() => {
    loadDistributors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = useCallback(async ({
    pagination,
    filterRaw,
  }: QueryTableParams<DistributorSkuRow>) => {
    await loadDistributors();

    const response = await api.sku.getDistributorSkus({
      limit: pagination.pageSize,
      offset: pagination.pageSize * (pagination.page - 1),
      is_active: toBooleanFilter(filterRaw.is_active),
      is_matched: toBooleanFilter(filterRaw.is_matched),
      distributor_id: filterRaw.distributor_id
        ? Number(filterRaw.distributor_id)
        : undefined,
      distributor_sku_code: filterRaw.distributor_sku_code
        ? String(filterRaw.distributor_sku_code)
        : undefined,
      distributor_sku_title: filterRaw.distributor_sku_title
        ? String(filterRaw.distributor_sku_title)
        : undefined,
      sku_code: filterRaw.sku_code ? String(filterRaw.sku_code) : undefined,
      sku_title: filterRaw.sku_title ? String(filterRaw.sku_title) : undefined,
      cacheForce: true,
    });

    const items = response?.data?.data || [];

    return {
      code: response?.code,
      total: response?.data?.total || 0,
      data: items.map((item) => ({
        ...item,
        distributor_name: distributorNamesRef.current.get(Number(item.distributor_id)) || '',
      })),
      message: response?.message,
    };
  }, [api, loadDistributors]);

  const onClose = () => {
    setEditOpen(false);
    setCurrent(undefined);
  };

  return (
    <QueryTable<DistributorSkuRow>
      title="SKU Дистрибьюторов"
      columns={columns}
      fetchData={fetchData}
      onCreateClick={() => {
        setCurrent(undefined);
        setEditOpen(true);
      }}
    >
      <EditDistributorSku
        open={editOpen}
        initial={current}
        onClose={onClose}
      />
    </QueryTable>
  );
};

export default DistributorSkusTable;
